import { Overlay } from '@angular/cdk/overlay';
import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { OperationObserverService } from '@x/common/operation';
import { OrderRecreateDialogComponent } from '@x/ecommerce-shop/app/core/order/components/order-recreate-dialog/order-recreate-dialog.component';
import {
  IOrderRecreateConfig,
  IRecreateDialogData,
  IRecreateResponse,
  TNavigateTo,
} from '@x/ecommerce-shop/app/core/order/services/order-recreate-config';
import {
  OrderSetShopOrder,
  OrderUpdateCartItems,
} from '@x/ecommerce-shop/app/core/order/state/shop-order.actions';
import { ShopOrderState } from '@x/ecommerce-shop/app/core/order/state/shop-order.state';
import { ShopStorage } from '@x/ecommerce-shop/app/core/shop-storage/shop-storage.service';
import {
  IShopOrder,
  IShopUserOrderRecreate,
  ShopCheckoutOrderService,
  ShopOrderService,
  ShopUserService,
  TShopUserOrderItem,
} from '@x/ecommerce/shop-client';
import { firstValueFrom, of, switchMap } from 'rxjs';

@Injectable()
export class OrderRecreateService implements OnDestroy {
  private _q = this.operation.createQueue();
  get processing() {
    return this._q.isLoading;
  }

  qStatus$ = this._q.statusChanges();

  private _itemsCache: TShopUserOrderItem[] = [];

  constructor(
    private shopOrderService: ShopOrderService,
    private shopCheckoutOrderService: ShopCheckoutOrderService,
    private shopStorage: ShopStorage,
    private store: Store,
    private shopUserService: ShopUserService,
    private dialog: MatDialog,
    private operation: OperationObserverService,
    private overlay: Overlay,
  ) {
    this._q.observe().subscribe((operation) => {
      if (this._q.lastJob?.id === this._q.currentJob?.id && operation.isFinalState()) {
        this.clearCache();
      }
    });
  }

  ngOnDestroy(): void {
    this.clearCache();
  }

  clearCache() {
    this._itemsCache = [];
  }

  recreateOperation$(config: IOrderRecreateConfig) {
    return this._q.queue(of('start').pipe(switchMap(() => this.recreateOrder(config))));
  }

  async recreateOrder(config: IOrderRecreateConfig): Promise<IRecreateResponse | null> {
    const { recreateOrderId, currentOrderId, showRecreateResponseDialog } = config;
    if (!recreateOrderId) return null;

    const recreateOrderItems = await firstValueFrom(
      this.shopUserService.getUserOrderItems({ id: recreateOrderId }),
    );

    this._itemsCache = [...this._itemsCache, ...recreateOrderItems];

    if (currentOrderId) {
      const orderState = await this.fetchCurrentOrderState(currentOrderId);

      if (orderState === 'CART') {
        return this.updateCart(currentOrderId, recreateOrderItems, showRecreateResponseDialog);
      }

      if (orderState === 'QUOTED') {
        await this.cancelOrder(currentOrderId);
      }
    }

    return await this.recreate(recreateOrderId, showRecreateResponseDialog);
  }

  private async updateCart(
    currentOrderId: number,
    recreateOrderItems: TShopUserOrderItem[],
    showRecreateResponseDialog?: boolean,
  ): Promise<IRecreateResponse> {
    const updatedOrder = await this.addCartItems(currentOrderId, recreateOrderItems);

    let navigateTo: TNavigateTo | undefined;

    if (showRecreateResponseDialog) {
      navigateTo = await this.openOrderStatusDialog(updatedOrder);
    }

    return { order: updatedOrder, navigateTo };
  }

  private async recreate(
    recreateOrderId: number,
    showRecreateResponseDialog?: boolean,
  ): Promise<IRecreateResponse> {
    const newOrder = await firstValueFrom(
      this.shopUserService.reCreateOrder({ orderId: recreateOrderId }),
    );

    await this.updateState(newOrder);

    let navigateTo: TNavigateTo | undefined;
    if (this._q.lastJob?.id === this._q.currentJob?.id && showRecreateResponseDialog) {
      navigateTo = await this.openOrderStatusDialog(newOrder.order);
    }

    return { order: newOrder.order, navigateTo };
  }

  private async addCartItems(currentOrderId: number, items: TShopUserOrderItem[]) {
    const input = {
      orderId: currentOrderId,
      items: items.map(({ variantId, quantity }) => ({ productVariantId: variantId, quantity })),
      context: { origin: 'order-history' },
    };

    return firstValueFrom(
      this.store
        .dispatch(new OrderUpdateCartItems(input))
        .pipe(switchMap(() => this.store.selectOnce(ShopOrderState.order))),
    );
  }

  private async openOrderStatusDialog(order: IShopOrder | null): Promise<TNavigateTo | undefined> {
    if (!order) return undefined;

    if (this._q.currentJob?.id !== this._q.lastJob?.id) return undefined;

    const itemsAdded = this._itemsCache.filter((item) =>
      order.items.some((orderItem) => orderItem.variantId === item.variantId),
    );

    return firstValueFrom(
      this.dialog
        .open<
          OrderRecreateDialogComponent,
          IRecreateDialogData,
          TNavigateTo
        >(OrderRecreateDialogComponent, { data: { itemsAdded }, panelClass: 'fullscreen-dialog-mobile', scrollStrategy: this.overlay.scrollStrategies.block() })
        .afterClosed(),
    );
  }

  private async updateState(newOrder: IShopUserOrderRecreate) {
    this.shopStorage.orderToken = newOrder.auth;
    await firstValueFrom(this.store.dispatch(new OrderSetShopOrder(newOrder.order)));
  }

  private fetchCurrentOrderState(orderId: number) {
    return firstValueFrom(this.shopOrderService.fetchOrderState({ id: orderId }));
  }

  private cancelOrder(orderId: number) {
    return firstValueFrom(this.shopCheckoutOrderService.cancel({ orderId }));
  }
}
